import React from "react";
import {Box, ButtonLink, Heading, Stack, Text, View, usePatronTheme} from "@unibuddy/patron";
import styled from "styled-components";
import {useAnalytics} from "@unibuddy/tracking";

import {
    AmbassadorProfileInformation,
    Buddies,
    StaffProfileInformation,
} from "../../../types/generated";
import {events} from "../../../analytics/productSpecification";
import {useAssistantInitiationContext} from "../providers/AssistantInitiationProvider";
import {useUniversityContext} from "../../../providers/UniversityProvider";

export type ChatAmbassadorProfileProps = {
    metadata: AmbassadorProfileInformation | StaffProfileInformation;
};

const StyledBox = styled(Box)`
    border-radius: 0 8px 8px;
`;

export function ChatAmbassadorProfile({metadata}: Readonly<ChatAmbassadorProfileProps>) {
    const theme = usePatronTheme();
    const {trackEvent} = useAnalytics();
    const {conversationId} = useAssistantInitiationContext();
    const {universitySlug} = useUniversityContext();

    return (
        <View
            position="relative"
            borderRadius="small"
            padding="medium-large"
            maxW={["75vw", 400]}
            minW="auto"
            borderWidth={1}
            borderColor="grey150"
            bgColor="white"
        >
            <Stack space="medium">
                <Box display="flex" gap="small" alignItems="center">
                    <Box h="85px" w="85px" position="relative" flexShrink={0}>
                        <Box
                            flexShrink={0}
                            as="img"
                            /* eslint-disable */
                            alt={`Profile picture, ${metadata.firstName}`}
                            borderRadius="full"
                            height="100%"
                            width="100%"
                            src={metadata.profilePhoto}
                        />
                        <Box
                            position="absolute"
                            bottom="0"
                            left="0"
                            right="0"
                            marginX="auto"
                            bgColor="grey900"
                            width="fit-content"
                            borderRadius="full"
                            padding="xsmall"
                        >
                            <Text align="center" size="small" color="white" truncate={1}>
                                {metadata.accountRole === Buddies.Ambassador ? "Student" : "Staff"}
                            </Text>
                        </Box>
                    </Box>
                    <Stack space="small-medium">
                        <Heading level="3" size="xxsmall" color="grey950" truncate={1}>
                            {metadata.firstName}
                        </Heading>
                        <Text color="grey800" truncate={1}>
                            {metadata.accountRole === Buddies.Ambassador
                                ? (metadata as AmbassadorProfileInformation).degreeName
                                : (metadata as StaffProfileInformation).role}
                        </Text>
                        {metadata.accountRole === Buddies.Ambassador && (
                            <Text color="grey800" truncate={1}>
                                {(metadata as AmbassadorProfileInformation).countryName}
                            </Text>
                        )}
                    </Stack>
                </Box>
                <StyledBox bgColor="grey50" padding="small">
                    <Text truncate={4}>{metadata.bio}</Text>
                </StyledBox>
                <ButtonLink
                    color="primary"
                    ghost
                    to={metadata.profileUrl}
                    onClick={() => {
                        trackEvent(events.assistantChatNowClicked.name, {
                            product: "Assistant",
                            conversationId,
                            buddyType: metadata.accountRole === Buddies.Staff ? "Staff" : "Student",
                            universitySlug,
                        });
                    }}
                    external
                    block
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    <Text weight="bold" color={theme.colors.primaryColor}>
                        Chat to {metadata.firstName}
                    </Text>
                </ButtonLink>
            </Stack>
        </View>
    );
}
